import { format } from 'date-fns';
import { useMemo } from 'react';
import { Event, eventTypeMap, WeddingMainEvent } from '@bridebook/web/lib/events/types';
import { getWeddingVenue } from '@bridebook/web/lib/shortlist/selectors';
import {
  DATE_FORMAT,
  dateFromIDatePicker,
} from '@bridebook/web/lib/time-date-manipulation/time-date-manipulation';
import { useSelector } from '@bridebook/web/lib/utils';
import { useWeddingEvents } from '@bridebook/web/lib/wedding-website/hooks/query';
import { getWeddingDate } from '@bridebook/web/lib/weddings/selectors';
import isDateExact from '@bridebook/web/lib/weddings/utils/is-date-exact';
import { getMainEvents } from './utils';

export const useMainEvent = () => {
  const weddingVenue = useSelector(getWeddingVenue);
  const { data: mainEventArray, isLoading } = useWeddingEvents(getMainEvents);
  const mainEvent = mainEventArray?.[0];

  const weddingDate = useSelector(getWeddingDate);
  const exactDate = useMemo(() => {
    if (weddingDate && isDateExact(weddingDate)) {
      return dateFromIDatePicker(weddingDate);
    }
    return null;
  }, [weddingDate]);

  // this is the main event with the date and supplier. If there is no date it will be null
  const mainEventWithWeddingData: Event | null = useMemo(() => {
    if (!mainEvent || !exactDate) return null;
    return {
      ...mainEvent,
      type: eventTypeMap.MAIN_EVENT,
      id: mainEvent.id as WeddingMainEvent['id'],
      supplier: weddingVenue?.id || '',
      date: format(exactDate, DATE_FORMAT.DATE.ISO),
    };
  }, [mainEvent, weddingVenue, exactDate]);

  // Main Event unified to match the type of the other events. Date and supplier can be empty strings
  const unifiedMainEvent: Event | null = useMemo(() => {
    if (!mainEvent) return null;
    return {
      ...mainEvent,
      type: eventTypeMap.MAIN_EVENT,
      id: mainEvent.id as WeddingMainEvent['id'],
      supplier: weddingVenue?.id || '',
      date: exactDate ? format(exactDate, DATE_FORMAT.DATE.ISO) : '',
    };
  }, [mainEvent, exactDate, weddingVenue]);

  return {
    mainEvent,
    mainEventWithWeddingData,
    unifiedMainEvent,
    date: exactDate,
    venue: weddingVenue,
    isLoading,
  };
};
